import { useState } from 'react'

import type { RequestLog } from '@engine/models'

import { ExpandableJsonPanel } from '#components/ExpandableJsonPanel'
import { formatDateTime, formatDuration } from '#utils/format'

interface RequestLogSectionsProps {
  requestLog: RequestLog
}

const getDurationColor = (duration?: number): string => {
  if (!duration) return 'text-base-content'
  if (duration < 100) return 'text-success'
  if (duration < 500) return 'text-info'
  if (duration < 1000) return 'text-warning'
  return 'text-error'
}

interface CollapsibleSectionProps {
  id: string
  title: string
  timestamp?: string
  duration?: number
  isExpanded: boolean
  onToggle: () => void
  data: unknown
  colorScheme: {
    bg: string
    border: string
    dot: string
    text: string
    preClass: string
  }
  additionalInfo?: React.ReactNode
}

const CollapsibleSection = ({
  title,
  timestamp,
  duration,
  isExpanded,
  onToggle,
  data,
  colorScheme,
  additionalInfo,
}: CollapsibleSectionProps) => {
  return (
    <div
      className={`rounded-box ${colorScheme.bg} border ${colorScheme.border}`}
    >
      <div
        className={`p-6 cursor-pointer hover:${colorScheme.bg.replace('/10', '/5')} transition-colors`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${colorScheme.dot}`} />
          <h3 className={`text-lg font-bold ${colorScheme.text}`}>{title}</h3>
          {timestamp && (
            <span className={`text-xs ${colorScheme.text}/70 ml-auto`}>
              {timestamp}
            </span>
          )}
          {duration && (
            <span
              className={`text-sm font-semibold ${timestamp ? '' : 'ml-auto'} ${getDurationColor(duration)}`}
            >
              {formatDuration(duration)}
            </span>
          )}
          <svg
            className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
      {isExpanded && (
        <div className="px-6 pb-6">
          {additionalInfo}
          <ExpandableJsonPanel
            title=""
            data={data}
            maxHeight="max-h-96"
            preClassName={colorScheme.preClass}
          />
        </div>
      )}
    </div>
  )
}

interface TenantRequestSectionProps {
  requestLog: RequestLog
  isExpanded: boolean
  onToggle: () => void
}

const TenantRequestSection = ({
  requestLog,
  isExpanded,
  onToggle,
}: TenantRequestSectionProps) => {
  if (!requestLog.tenantRequest?.content) return null

  return (
    <CollapsibleSection
      id="tenantRequest"
      title="Tenant Request"
      timestamp={formatDateTime(requestLog.tenantRequest.requestDate)}
      isExpanded={isExpanded}
      onToggle={onToggle}
      data={requestLog.tenantRequest.content}
      colorScheme={{
        bg: 'bg-blue-500/10',
        border: 'border-blue-500/30',
        dot: 'bg-blue-400',
        text: 'text-blue-300',
        preClass:
          'bg-slate-950/80 border border-blue-500/20 font-mono text-blue-100',
      }}
    />
  )
}

interface ServiceRequestSectionProps {
  service: string
  requestDate: string
  content: unknown
  isExpanded: boolean
  onToggle: () => void
}

const ServiceRequestSection = ({
  service,
  requestDate,
  content,
  isExpanded,
  onToggle,
}: ServiceRequestSectionProps) => {
  return (
    <CollapsibleSection
      id={`serviceRequest-${service}`}
      title={`Service Request - ${service}`}
      timestamp={formatDateTime(requestDate)}
      isExpanded={isExpanded}
      onToggle={onToggle}
      data={content}
      colorScheme={{
        bg: 'bg-cyan-500/10',
        border: 'border-cyan-500/30',
        dot: 'bg-cyan-400',
        text: 'text-cyan-300',
        preClass:
          'bg-slate-950/80 border border-cyan-500/20 font-mono text-cyan-100',
      }}
    />
  )
}

interface ServiceResponseSectionProps {
  service: string
  responseTime?: number
  content: unknown
  isExpanded: boolean
  onToggle: () => void
}

const ServiceResponseSection = ({
  service,
  responseTime,
  content,
  isExpanded,
  onToggle,
}: ServiceResponseSectionProps) => {
  return (
    <CollapsibleSection
      id={`serviceResponse-${service}`}
      title={`Service Response - ${service}`}
      duration={responseTime}
      isExpanded={isExpanded}
      onToggle={onToggle}
      data={content}
      colorScheme={{
        bg: 'bg-emerald-500/10',
        border: 'border-emerald-500/30',
        dot: 'bg-emerald-400',
        text: 'text-emerald-300',
        preClass:
          'bg-slate-950/80 border border-emerald-500/20 font-mono text-emerald-100',
      }}
    />
  )
}

interface TenantResponseSectionProps {
  requestLog: RequestLog
  isExpanded: boolean
  onToggle: () => void
}

const TenantResponseSection = ({
  requestLog,
  isExpanded,
  onToggle,
}: TenantResponseSectionProps) => {
  if (!requestLog.tenantResponse?.content) return null

  return (
    <CollapsibleSection
      id="tenantResponse"
      title="Tenant Response"
      duration={requestLog.tenantResponse.responseTime}
      isExpanded={isExpanded}
      onToggle={onToggle}
      data={requestLog.tenantResponse.content}
      colorScheme={{
        bg: 'bg-green-500/10',
        border: 'border-green-500/30',
        dot: 'bg-green-400',
        text: 'text-green-300',
        preClass:
          'bg-slate-950/80 border border-green-500/20 font-mono text-green-100',
      }}
    />
  )
}

interface TenantCallbackSectionProps {
  webHookURI: string
  auth: string
  callbackDate?: string
  responseStatusCode: number
  content: unknown
  isExpanded: boolean
  onToggle: () => void
}

const TenantCallbackSection = ({
  webHookURI,
  auth,
  callbackDate,
  responseStatusCode,
  content,
  isExpanded,
  onToggle,
}: TenantCallbackSectionProps) => {
  return (
    <CollapsibleSection
      id={`tenantCallback-${responseStatusCode}`}
      title="Tenant Callback"
      timestamp={`Status: ${responseStatusCode}`}
      isExpanded={isExpanded}
      onToggle={onToggle}
      data={content}
      colorScheme={{
        bg: 'bg-orange-500/10',
        border: 'border-orange-500/30',
        dot: 'bg-orange-400',
        text: 'text-orange-300',
        preClass:
          'bg-slate-950/80 border border-orange-500/20 font-mono text-orange-100',
      }}
      additionalInfo={
        <div className="mb-3 text-xs text-orange-300/70 space-y-1">
          <div>Webhook URI: {webHookURI}</div>
          <div>Auth: {auth}</div>
          {callbackDate && <div>Date: {formatDateTime(callbackDate)}</div>}
        </div>
      }
    />
  )
}

const RequestLogSections = ({ requestLog }: RequestLogSectionsProps) => {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({})

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }))
  }

  return (
    <>
      <TenantRequestSection
        requestLog={requestLog}
        isExpanded={expandedSections['tenantRequest'] || false}
        onToggle={() => toggleSection('tenantRequest')}
      />

      {requestLog.serviceRequest?.map((serviceReq, index) => (
        <ServiceRequestSection
          key={`service-req-${index}`}
          service={serviceReq.service}
          requestDate={serviceReq.requestDate}
          content={serviceReq.content}
          isExpanded={expandedSections[`serviceRequest-${index}`] || false}
          onToggle={() => toggleSection(`serviceRequest-${index}`)}
        />
      ))}

      {requestLog.serviceResponse?.map((serviceRes, index) => (
        <ServiceResponseSection
          key={`service-res-${index}`}
          service={serviceRes.service}
          responseTime={serviceRes.responseTime}
          content={serviceRes.content}
          isExpanded={expandedSections[`serviceResponse-${index}`] || false}
          onToggle={() => toggleSection(`serviceResponse-${index}`)}
        />
      ))}

      <TenantResponseSection
        requestLog={requestLog}
        isExpanded={expandedSections['tenantResponse'] || false}
        onToggle={() => toggleSection('tenantResponse')}
      />

      {requestLog.tenantCallback?.map((callback, index) => (
        <TenantCallbackSection
          key={`callback-${index}`}
          webHookURI={callback.webHookURI}
          auth={callback.auth}
          callbackDate={callback.callbackDate}
          responseStatusCode={callback.responseStatusCode}
          content={callback.content}
          isExpanded={expandedSections[`tenantCallback-${index}`] || false}
          onToggle={() => toggleSection(`tenantCallback-${index}`)}
        />
      ))}
    </>
  )
}

export { RequestLogSections }
